import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, Input, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatSelectModule } from '@angular/material/select';
import {
  CrmObjectService,
  CrmPipelineService,
  FieldValueInterface,
  ObjectType,
  Pipeline,
  ProfileHeaderComponentInterface,
  Stage,
  StandardIds,
  UpdaterService,
} from '@galaxy/crm/static';
import { CrmObjectInterface, FieldValue } from '@vendasta/crm';
import { distinctUntilChanged, firstValueFrom } from 'rxjs';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

@Component({
  templateUrl: './stage-selector.component.html',
  styleUrls: ['./stage-selector.component.scss'],
  imports: [
    CommonModule,
    MatSelectModule,
    GalaxyFormFieldModule,
    TranslateModule,
    ReactiveFormsModule,
    GalaxyLoadingSpinnerModule,
  ],
})
export class CrmStageSelectorComponent implements ProfileHeaderComponentInterface {
  @Input() crmObjectType: ObjectType | undefined;

  private readonly _crmObject = signal<CrmObjectInterface | undefined>(undefined);
  @Input()
  set crmObject(value: CrmObjectInterface) {
    this._crmObject.set(value);
  }
  get crmObject(): CrmObjectInterface | undefined {
    return this._crmObject();
  }

  private readonly pipelineService = inject(CrmPipelineService);
  private readonly updaterService = inject(UpdaterService);
  private readonly objectService = inject(CrmObjectService);
  private readonly snackbarService = inject(SnackbarService);

  readonly pipelineId = computed(() => this.computePipelineId(this._crmObject()));
  readonly pipeline = signal<Pipeline | undefined>(undefined);
  readonly stageId = computed(() => this.computeStageId(this._crmObject()));
  readonly stages = computed(() => this.pipeline()?.stages ?? []);
  readonly stageCtrl = new FormControl<string | undefined>(undefined, { nonNullable: true });

  constructor() {
    effect(() => this.loadPipeline(this.pipelineId()));
    effect(() => this.stageCtrl.setValue(this.stageId()));
    this.stageCtrl.valueChanges
      .pipe(takeUntilDestroyed(), distinctUntilChanged())
      .subscribe((stageId) => this.updateStageId(stageId));
    this.updaterService.updatedField
      .pipe(takeUntilDestroyed())
      .subscribe((fieldChanged) => this.refreshObjectField(fieldChanged));
  }

  private computePipelineId(crmObject: CrmObjectInterface | undefined): string {
    return crmObject?.fields?.find((f) => f.fieldId === StandardIds.OpportunityPipelineID)?.stringValue ?? '';
  }

  private computeStageId(crmObject: CrmObjectInterface | undefined): string {
    return crmObject?.fields?.find((f) => f.fieldId === StandardIds.OpportunityCalculatedStageID)?.stringValue ?? '';
  }

  private refreshObjectField(field: FieldValueInterface): void {
    const crmObject = this._crmObject();
    if (!crmObject) {
      return;
    }
    if (field.fieldId === StandardIds.OpportunityCalculatedStageID) {
      const stageId = field.stringValue;
      const currentStageId = this.stageId();
      if (stageId !== currentStageId) {
        const otherFields =
          crmObject.fields?.filter((f) => f.fieldId !== StandardIds.OpportunityCalculatedStageID) ?? [];
        const stageField = new FieldValue({ fieldId: StandardIds.OpportunityCalculatedStageID, stringValue: stageId });
        crmObject.fields = [...otherFields, stageField];
        this._crmObject.set({ ...crmObject });
      }
    }
  }

  private updateStageId(stageId?: string): void {
    const crmObject = this._crmObject();
    if (this.crmObjectType && crmObject) {
      const currentValue = this.stageId();
      const stage = this.stages().find((s) => s.id === stageId);
      if (!stage || currentValue === stageId) {
        return;
      }
      // update field value
      this.mutate(this.crmObjectType, crmObject, stage).then(() => {
        // propagate changes
        const stageField: FieldValueInterface = {
          fieldId: StandardIds.OpportunityCalculatedStageID,
          stringValue: stageId,
        };
        const currentField = crmObject.fields?.find((f) => f.fieldId === StandardIds.OpportunityCalculatedStageID);
        if (currentField) {
          currentField.stringValue = stageId;
        } else {
          if (!crmObject.fields) {
            crmObject.fields = [];
          }
          crmObject.fields.push(stageField);
        }
        this._crmObject.set({ ...crmObject });
        this.updaterService.updateFieldValue(stageField);
      });
    }
  }

  private async mutate(objectType: ObjectType, crmObject: CrmObjectInterface, stage: Stage): Promise<void> {
    const changes: CrmObjectInterface = {
      crmObjectId: crmObject.crmObjectId,
      fields: stage.mutations,
    };
    try {
      await firstValueFrom(this.objectService.updateCrmObject(objectType, changes));
      stage.mutations.forEach((mutation) => this.updaterService.updateFieldValue(mutation));
      this.snackbarService.openSuccessSnack('OPPORTUNITY.EDIT_PAGE.SAVE.SUCCESS');
    } catch {
      this.snackbarService.openErrorSnack('ERRORS.GENERIC_EDIT_MESSAGE');
    }
  }

  private async loadPipeline(pipelineId?: string): Promise<void> {
    if (pipelineId) {
      try {
        const pipeline = await firstValueFrom(this.pipelineService.getPipeline$(pipelineId));
        this.pipeline.set(pipeline);
      } catch {
        console.error('Failed to load pipeline');
        this.pipeline.set(undefined);
      }
    } else {
      this.pipeline.set(undefined);
    }
  }
}
